/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { ExclamationCircleOutlined, TeamOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { getJerseyConflicts } from '@sportninja/common/features/game/attendance/utils/attendanceDerivations'
import React from 'react'
import EmptyStateBox from 'src/components/ts-components/EmptyStateBox'
import ScrollContainer from 'src/components/ts-components/ScrollContainer'
import AttendanceSection from './components/AttendanceSection'
import AttendanceSkeleton from './components/AttendanceSkeleton'
import AttendanceStatusCard from './components/AttendanceStatusCard'
import { useAttendanceGameMeta } from './hooks/useAttendanceGameMeta'
import { useAttendanceMutations } from './hooks/useAttendanceMutations'
import { useAttendanceSort } from './hooks/useAttendanceSort'
import { useGameAttendancePlayers } from './hooks/useGameAttendancePlayers'
import { useScoringContextSettings } from './hooks/useScoringContextSettings'
import { ELIGIBLE_GAME_SPARES_NOTE } from '@sportninja/common/features/game/attendance/constants'
import { isWaiverEnforced } from '@sportninja/common/features/game/attendance/utils/waiverLock'
import type { AttendancePlayer } from './types'
import {
  getEligibleGameSpares,
  getPlayersAndAffiliates,
  getPromotedCandidateRequestById,
} from './utils/sections'

export interface ManageTeamAttendanceModalContentProps {
  gameId: string
  rosterId: string
  // The game's show_waiver flag (from the game-list row).
  showWaiver: boolean
  soccerSportId?: number
}

// Inner padding for the whole modal body (the modal itself uses
// removeBodyPaddingX for edge-to-edge, so we add breathing room here).
const containerStyle = css`
  padding: 0 16px;
`

// ScrollContainer owns the overflow and draws the always-visible custom
// scrollbar; this only caps its height.
const scrollAreaStyle = css`
  max-height: 60vh;
`

const sectionsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const errorStyle = css`
  color: ${colors.ERROR_200};
  padding: 24px;
`

// Duplicate-jersey warning banner (dark modal). Mirrors the previous modal's
// alert styling.
const jerseyWarningStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  padding: 8px;
  border-radius: 8px;
  background-color: #360000;
  border: 1px solid ${colors.ERROR_200};
  color: ${colors.ERROR_100};
  font-size: 14px;
  line-height: 20px;
`

/**
 * Assembles the Manage Team Attendance modal body: summary card + the three
 * attendance sections (Roster, Roster Spares, Eligible Game Spares).
 */
const ManageTeamAttendanceModalContent: React.FC<
  ManageTeamAttendanceModalContentProps
> = ({ gameId, rosterId, showWaiver, soccerSportId }) => {
  const { players, spareCandidates, loading, error } = useGameAttendancePlayers(
    { gameId, rosterId }
  )
  const { requireWaiverSignature, requireUniqueJerseyNumbers } =
    useScoringContextSettings(gameId)
  const waiverEnforced = isWaiverEnforced(showWaiver, requireWaiverSignature)
  const { isGenericSport, isSoccer } = useAttendanceGameMeta(
    gameId,
    soccerSportId
  )
  const rosterSort = useAttendanceSort()
  const spareSort = useAttendanceSort()
  const candidateSort = useAttendanceSort()
  const {
    updateAttendanceStatus,
    promoteSpare,
    demoteSpare,
    withdrawSpare,
    updatePlayerNumber,
    updateStartingChange,
  } = useAttendanceMutations({ gameId, rosterId })

  const { skaters, affiliates } = getPlayersAndAffiliates(players)
  const eligibleSpares = getEligibleGameSpares(spareCandidates)
  const promotedRequestById = getPromotedCandidateRequestById(spareCandidates)

  // getJerseyConflicts' local AttendancePlayer type models player_number as
  // `number | null`; this modal's AttendancePlayer allows `number | string`
  // (raw API shape). Structurally compatible at runtime — cast at the call site.
  const conflicts = getJerseyConflicts(
    players as unknown as Parameters<typeof getJerseyConflicts>[0]
  )
  // getJerseyConflicts returns occurrence-count-minus-one per number (0 = unique,
  // 1 = two players share it, …), matching the mobile consumer's `> 0` check.
  // So `> 0` flags any shared number, including the common two-player case.
  const duplicateJerseyNumbers = Object.keys(conflicts)
    .filter((key) => conflicts[key] > 0)
    .map((key) => Number(key))
  // Warn only when the game requires unique numbers (same gate as the per-row
  // red highlight).
  const showJerseyWarning =
    requireUniqueJerseyNumbers && duplicateJerseyNumbers.length > 0

  const onPressAttendanceStatus = (playerId: string, next: number | null) =>
    updateAttendanceStatus(playerId, next).catch(() => {})

  const onPressRow = (_player: AttendancePlayer) => {
    // No player-detail sheet on web yet.
  }

  const onStartingChange = (
    playerId: string,
    isStarting: boolean,
    otherGoalies: AttendancePlayer[]
  ) => updateStartingChange(playerId, isStarting, undefined, otherGoalies)

  if (loading) {
    return <AttendanceSkeleton />
  }

  if (error) {
    return <div css={errorStyle}>{error}</div>
  }

  return (
    <div css={containerStyle}>
      <AttendanceStatusCard players={players} />
      {showJerseyWarning && (
        <div css={jerseyWarningStyle}>
          <ExclamationCircleOutlined />
          {`You Have ${duplicateJerseyNumbers.length} ${
            duplicateJerseyNumbers.length === 1 ? 'Player' : 'Players'
          } With The Same Jersey Number. Click A Number To Change It.`}
        </div>
      )}
      {/* Nothing in any section (no roster players, no eligible spares) →
          one boxed empty state instead of a blank body. Copy is shared with
          mobile's empty attendance screen. */}
      {players.length === 0 && eligibleSpares.length === 0 ? (
        <EmptyStateBox
          icon={<TeamOutlined />}
          title='No Players'
          subtitle={t('GameSetup:noPlayers', {})}
        />
      ) : (
        <ScrollContainer css={scrollAreaStyle} mirrorTrackGutter>
          <div css={sectionsStyle}>
            <AttendanceSection
              label='Roster'
              count={skaters.length}
              list={skaters}
              sort={rosterSort}
              waiverEnforced={waiverEnforced}
              requireUniqueJerseyNumbers={requireUniqueJerseyNumbers}
              duplicateJerseyNumbers={duplicateJerseyNumbers}
              isGenericSport={isGenericSport}
              isSoccer={isSoccer}
              allPlayers={players}
              forceConfirmed
              hideConfirm
              onPressAttendanceStatus={onPressAttendanceStatus}
              onPressRow={onPressRow}
              onUpdatePlayerNumber={updatePlayerNumber}
              onStartingChange={onStartingChange}
            />
            <AttendanceSection
              label='Roster Spares'
              count={affiliates.length}
              list={affiliates}
              sort={spareSort}
              waiverEnforced={waiverEnforced}
              requireUniqueJerseyNumbers={requireUniqueJerseyNumbers}
              duplicateJerseyNumbers={duplicateJerseyNumbers}
              isGenericSport={isGenericSport}
              isSoccer={isSoccer}
              allPlayers={players}
              forceConfirmed
              availYesNoOnly
              spareCandidatesLast
              promotedRequestById={promotedRequestById}
              onPressAttendanceStatus={onPressAttendanceStatus}
              onPressRow={onPressRow}
              onUpdatePlayerNumber={updatePlayerNumber}
              onStartingChange={onStartingChange}
              onDemoteSpare={demoteSpare}
            />
            <AttendanceSection
              label='Eligible Game Spares'
              note={ELIGIBLE_GAME_SPARES_NOTE}
              count={eligibleSpares.length}
              list={eligibleSpares}
              sort={candidateSort}
              waiverEnforced={waiverEnforced}
              requireUniqueJerseyNumbers={requireUniqueJerseyNumbers}
              duplicateJerseyNumbers={duplicateJerseyNumbers}
              isGenericSport={isGenericSport}
              isSoccer={isSoccer}
              allPlayers={players}
              isSpareCandidate
              onPressAttendanceStatus={onPressAttendanceStatus}
              onPressRow={onPressRow}
              onUpdatePlayerNumber={updatePlayerNumber}
              onStartingChange={onStartingChange}
              onPromoteSpare={promoteSpare}
              onDemoteSpare={demoteSpare}
              onWithdrawSpare={withdrawSpare}
            />
          </div>
        </ScrollContainer>
      )}
    </div>
  )
}

export default ManageTeamAttendanceModalContent
